import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { ReactComponent as SearchIcon } from "../../../images/icons/forms/searchIcon.svg";
import classes from "./Suggestion.module.css";
import ValidationErrors from "../ValidationErrors/ValidationErrors";
import useHttpFetch from "../../../hooks/useHttpFetch";
import sortOptions from "./sortOptions";
import { toast } from "react-toastify";
import textExtracer from "./textExtracer";
import validate from "../../../utils/validations/validateV2";
import MultiLanTxt from "../MultiLanTxt/MultiLanTxt";
import checkFieldDisabledStatus from "../../../utils/accessUtils/checkFieldDisabledStatus";
import extractFieldValidations from "../../../utils/accessUtils/extractFieldValidations";
import useDelayUnmount from "../../../hooks/useDelayUnmount";
import { createPortal } from "react-dom";
// import { ReactComponent as MultipyIcon } from "../../../images/icons/forms/multipyIcon.svg";
// import CheckOutlinedIcon from "@mui/icons-material/CheckOutlined";

const Suggestion = ({
  options,
  changeHandler,
  label,
  setOptions,
  name,
  value,
  disabled,
  processualDisabled,
  pageIsProcessual,
  entityIsProcessual,
  skipStateCanEditThisFieldCheck,
  validityName,
  setCopiedOptions,
  frontMultipy,
  copiedOptions,
  uniqueIdentifier,
  labelOptionKey,
  forceLtr,
  apiOptions,
  customFetchRequest,
  customFetchRequestIsLoading,
  customFetchDebounceDependencie,
  fieldAccesses,
  fieldNames = [],
  fieldName,
  clearInnerTextStateAfterSelect,
  style,
  currentState,
  forceEn,
  localValidationInfos = { type: "suggestion" },
  fieldAddress,
  indexArr,
  confirmActionOrder,
  validateHandler,
  changeSelectedValueHandler,
  frontSearch,
  onFocus,
  onBlur,
  indexToSendRequestAfterThat = 0,
  tooltipContent,
  skipFieldConfigs,
  skipUseHttpFetch,
  getOptionLabel,
  validationOnFront = false,
  isMongoField,
  hasSQLEntity,
  mongoFieldPath,
  withStar = true,
}) => {
  const [txt, setTxt] = useState("");
  const [open, setOpen] = useState(false);
  const showOptions = useDelayUnmount(open, 250);

  const [focusOn, setFocusOn] = useState(0);
  const [stopUnifying, setStopUnifying] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0, width: 0 });
  const triggerRef = useRef(null);

  const { disabled: fieldIsDisabled, processualDisabled: fieldIsProcessualDisabled } = useMemo(
    () =>
      checkFieldDisabledStatus({
        fieldAccesses,
        fieldName,
        pageIsProcessual,
        disabledFromParent: disabled,
        processualDisabledFromParent: processualDisabled,
        currentState,
        entityIsProcessual,
        skipStateCanEditThisFieldCheck,
        isMongoField,
        hasSQLEntity,
        mongoFieldPath: mongoFieldPath ? mongoFieldPath : fieldAddress ? fieldAddress : null,
      }),
    [fieldAccesses, disabled, processualDisabled, currentState],
  );
  const { inWhichStateOrderItBecomesNecessary, validationType, isMandatory } = useMemo(
    () =>
      extractFieldValidations({
        fieldAccesses,
        fieldName,
      }),
    [fieldAccesses],
  );

  const args = skipUseHttpFetch ? [false, "FNM"] : [];
  const { error, isLoading, sendRequest } = useHttpFetch(...args);
  let debounceTimeout = null;
  useEffect(() => {
    if (!stopUnifying) {
      setTxt(getOptionLabel ? getOptionLabel(value) : textExtracer(value, labelOptionKey));
    }
  }, [value, stopUnifying]);
  useLayoutEffect(() => {
    if (!open) return;
    const update = () => {
      const rect = triggerRef.current.getBoundingClientRect();
      setCoords({
        top: rect.bottom + window.scrollY,
        left: rect.left + window.scrollX,
        width: rect.width,
      });
    };
    window.addEventListener("scroll", update, true); // true = capture, catches inner scroll containers too
    window.addEventListener("resize", update);
    return () => {
      window.removeEventListener("scroll", update, true);
      window.removeEventListener("resize", update);
    };
  }, [open]);

  const fetchData = async (query) => {
    let properUrl;
    if (query.trim() !== "") {
      properUrl = apiOptions.url.replaceAll("%enteredTxtByUser%", query);
    } else {
      const regex = /([^?&]+)\?[^&]*%enteredTxtByUser%[^&]*&(.*)/;
      const match = regex.exec(apiOptions.url);
      properUrl = match
        ? `${match[1]}?${match[2]}`
        : apiOptions.url.replaceAll("%enteredTxtByUser%", query);
    }

    const properFetchOptions = {
      ...apiOptions,
      url: properUrl,
    };
    const result = await sendRequest(properFetchOptions);
    if (result?.resIsOk) {
      let sortOptionsAttribute = result.response;
      if (Array.isArray(result.response) && Array.isArray(result.response?.[0])) {
        sortOptionsAttribute = result.response.map((item) => item[0]);
      }
      const properOptions = sortOptions(sortOptionsAttribute);
      setOptions(properOptions);
      if (setCopiedOptions) {
        setCopiedOptions(structuredClone(properOptions));
      }
    } else {
      toast.error(forceLtr ? "Importing data Failed" : "دریافت اطلاعات با خطا مواجه شد", {
        toastId: "fetching-options-failed",
      });
    }
  };
  const getOptions = useCallback(
    (enteredTxt) => {
      if (debounceTimeout) {
        clearTimeout(debounceTimeout);
      }
      debounceTimeout = setTimeout(() => {
        // console.log("we are here1");
        if (customFetchRequest) {
          // console.log("we are here2");
          customFetchRequest(enteredTxt);
          // console.log("we are here5");
        } else {
          fetchData(enteredTxt);
        }
      }, 400);
    },
    [customFetchDebounceDependencie],
  );

  const searchInOptionsHandler = (enteredTxt) => {
    if (enteredTxt?.trim() !== "") {
      let filteredOptions = copiedOptions.filter((option) => {
        let searchIn;
        if (typeof option === "object")
          searchIn = getOptionLabel ? getOptionLabel(option) : textExtracer(option, labelOptionKey);
        else searchIn = option;

        return searchIn
          ?.toString()
          ?.toLowerCase()
          ?.replace(/\s/g, "")
          ?.includes(enteredTxt?.toLowerCase()?.replace(/\s/g, ""));
      });
      setOptions(filteredOptions);
    } else {
      setOptions(copiedOptions);
    }
  };

  const blurHandler = (e) => {
    if (onBlur) onBlur(e, fieldAddress);
    // if (open) setOpen(false);
    // if (validationType || isMandatory || localValidationInfos) {
    if (validateHandler) {
      const validateOptions = { ...localValidationInfos };
      if (!validationOnFront) {
        if (isMandatory) {
          validateOptions.isOptional = false;
        } else {
          validateOptions.isOptional = true;
        }
        // if (validationType) validateOptions.type = validationType; // SINCE THERE WILL BE NO TYPE FOR SUGGESTIONS
        if (!skipStateCanEditThisFieldCheck) {
          if (inWhichStateOrderItBecomesNecessary)
            validateOptions.inWhichStateOrderItBecomesNecessary = inWhichStateOrderItBecomesNecessary;
          if (confirmActionOrder) validateOptions.clickedActionOrder = confirmActionOrder;
        }
      }
      validateOptions.data = value ? value : e.target.value === "" ? "" : "notFromOptions";

      const newValue = validate(validateOptions);
      validateHandler(newValue, fieldAddress, indexArr);
    }
    if (!frontSearch) setOptions([]);
    // }
  };
  const keyDownHandler = (e) => {
    if (e.code === "Enter" || e.code === "NumpadEnter")
      if (options.length > 0) {
        let selectedOption = getOptionLabel
          ? getOptionLabel(options[focusOn])
          : textExtracer(options[focusOn], labelOptionKey);
        if (!clearInnerTextStateAfterSelect) setTxt(selectedOption);
        else setTxt("");
        if (stopUnifying) setStopUnifying(false);
        // changeHandler(options[focusOn], fieldAddress, indexArr);
        changeSelectedValueHandler(name, options[focusOn]);
        // if (validityName) validationBlur(true, name);
        if (validateHandler) {
          if (validityName?.isValid === false)
            validateHandler({ isValid: true, msg: "" }, fieldAddress, indexArr);
        }
        setOpen(false);
      }

    if (e.key === "ArrowDown") {
      e.preventDefault();
      if (focusOn !== options.length - 1) setFocusOn((prev) => prev + 1);
      if (focusOn === options.length - 1) setFocusOn(0);
    }
    if (e.key === "ArrowUp") {
      e.preventDefault();
      if (focusOn !== 0) setFocusOn((prev) => prev - 1);
      if (focusOn === 0) setFocusOn(options.length - 1);
    }
  };
  const onChange = async (e) => {
    setTxt(e.target.value);

    if (value) {
      if (!stopUnifying) setStopUnifying(true);
      changeSelectedValueHandler(name, null);
      // changeHandler(null, fieldAddress, indexArr);
    }
    if (!frontSearch) {
      if (e.target.value?.trim()?.length > indexToSendRequestAfterThat - 1) {
        getOptions(
          // e.target.value?.trim() === "" ? "a" :
          e.target.value,
        );
      } else {
        setOptions([]);
      }
    }
    if (frontSearch) searchInOptionsHandler(e.target.value);
    // if (validityName) if (validityName.isValid) validationBlur(false, name, "notFromOptions");
    if (validateHandler) {
      if (validityName)
        if (validityName.isValid) {
          validateHandler(
            {
              isValid: false,
              msg: forceEn ? "Choose from the options pls" : <MultiLanTxt tid="ENGLISH_MUST_OPTION" />,
            },
            fieldAddress,
            indexArr,
          );
        }
    }

    setFocusOn(0);
  };
  const focusHandler = (e) => {
    if (onFocus) onFocus(e, fieldAddress);
    const rect = triggerRef.current.getBoundingClientRect();
    setCoords({
      top: rect.bottom + window.scrollY,
      left: rect.left + window.scrollX,
      width: rect.width,
    });
    if (!open) setOpen(true);
    if (!frontSearch && e.target.value?.trim()?.length > indexToSendRequestAfterThat - 1) {
      getOptions(
        // e.target.value?.trim() === "" ? "a" :
        e.target.value,
      );
    }
  };

  const optionClickHandler = (e, option, indexz) => {
    let optionName = getOptionLabel ? getOptionLabel(option) : textExtracer(option, labelOptionKey);
    if (!clearInnerTextStateAfterSelect) setTxt(optionName);
    else setTxt("");
    setFocusOn(indexz);
    if (stopUnifying) setStopUnifying(false);
    // changeHandler(option, fieldAddress, indexArr);
    changeSelectedValueHandler(name, option);
    // if (validityName) validationBlur(true, name);
    if (validateHandler) {
      if (!validityName?.isValid) validateHandler({ isValid: true, msg: "" }, fieldAddress, indexArr);
    }
    setOpen(false);
  };
  const renderLabelOptions = (option) => {
    if (getOptionLabel) return getOptionLabel(option);

    if (Array.isArray(labelOptionKey)) {
      return labelOptionKey.map((key, index) => <span key={index}>{textExtracer(option, key)}</span>);
    }
    return textExtracer(option, labelOptionKey);
  };
  const readOnly = skipFieldConfigs ? disabled : fieldIsDisabled || fieldIsProcessualDisabled;
  return (
    <>
      {open
        ? createPortal(<div className={classes.bckdrp} onClick={() => setOpen(false)} />, document.body)
        : null}
      <div
        ref={triggerRef}
        className={style === "editableList" ? classes.editableListContainer : classes.container}
      >
        {/* {validityName ? (
        validityName?.isValid === false ? (
          <MultipyIcon className={classes.multipyIcon} />
        ) : value ? (
          <CheckOutlinedIcon className={classes.checkIcon} />
        ) : null
      ) : null} */}
        <label
          className={[
            classes.label,
            readOnly
              ? "disabledinputPLabel"
              : open
                ? [
                    "focusedinputPLabel",
                    "pLabelUp",
                    style === "editableList" ? classes.editableListLabelUp : classes.labelUp,
                  ].join(" ")
                : validityName
                  ? validityName?.isValid !== false
                    ? // (typeof value === "string" ? value?.toString()?.trim() === "" : value?.id === null) ||
                      //   value === null
                      //   ? "enabledinputPLabel"
                      //   : "validinputPLabel"
                      "enabledinputPLabel"
                    : (typeof value === "string"
                          ? value?.toString()?.trim() === ""
                          : value?.id === null) || value === null
                      ? "invalidinputPLabel"
                      : "invalidinputPLabel"
                  : (typeof value === "string"
                        ? value?.toString()?.trim() === ""
                        : value?.id === null) || value === null
                    ? "enabledinputPLabel"
                    : "enabledinputPLabel",
            txt?.toString()?.trim() !== "" || open
              ? style === "editableList"
                ? [classes.editableListLabelUp, "pLabelUp"].join(" ")
                : [classes.labelUp, "pLabelUp"].join(" ")
              : style === "editableList"
                ? [classes.editableListLabelDown, "pLabelDown"].join(" ")
                : [classes.labelDown, "pLabelDown"].join(" "),
          ].join(" ")}
          htmlFor={uniqueIdentifier ? uniqueIdentifier : name}
        >
          {label}
          {withStar ? (
            isMandatory || localValidationInfos?.isOptional === false ? (
              <span className={classes.mandatorySign}>*</span>
            ) : null
          ) : null}
        </label>
        <label
          className={[
            classes.magnifier,
            readOnly
              ? "disabledinputPLabel"
              : open
                ? "focusedinputPLabel"
                : validityName
                  ? validityName?.isValid !== false
                    ? "enabledinputPLabel"
                    : "invalidinputPLabel"
                  : "enabledinputPLabel",
            txt?.toString()?.trim() !== "" || open
              ? style === "editableList"
                ? classes.editableListMagnifierUp
                : classes.magnifierUp
              : style === "editableList"
                ? classes.editableListMagnifierDown
                : classes.magnifierDown,
          ].join(" ")}
        >
          <SearchIcon />
        </label>
        {tooltipContent ? <span className={classes.tooltip}>{tooltipContent}</span> : null}
        <input
          disabled={readOnly}
          autoComplete="off"
          className={[
            "pInput",
            style === "editableList" ? classes.txtInputEditableList : classes.txtInput,
            readOnly
              ? "disabledPInput"
              : open
                ? "focusedPInput"
                : validityName
                  ? validityName?.isValid !== false
                    ? //  (typeof value === "string" ? value?.toString()?.trim() === "" : value?.id === null) ||
                      //   value === null
                      //   ? "enabledPInput"
                      //   : "validPInput"
                      "enabledPInput"
                    : "invalidPInput"
                  : "enabledPInput",
          ].join(" ")}
          value={txt}
          id={uniqueIdentifier ? uniqueIdentifier : name}
          type="text"
          name={name}
          onFocus={focusHandler}
          onChange={onChange}
          onKeyDown={keyDownHandler}
          onBlur={blurHandler}
        />
        {showOptions
          ? createPortal(
              <div
                className={[
                  style === "editableList"
                    ? classes.optionContainerEditableList
                    : classes.optionContainer,
                  open ? classes.mounted : classes.unmounted,
                ].join(" ")}
                style={{
                  position: "absolute",
                  top: coords.top,
                  left: coords.left,
                  width: coords.width,
                  // zIndex: 9999,
                }}
              >
                {isLoading || customFetchRequestIsLoading ? (
                  <p
                    className={[classes.option, "pLabelUp", "enabledinputPLabel"].join(" ")}
                    style={forceLtr ? { textAlign: "end" } : { textAlign: "start" }}
                  >
                    {forceLtr ? "... Loading" : "در حال بررسی ..."}
                  </p>
                ) : options.length > 0 ? (
                  options.map((option, indexz) => (
                    <p
                      name={option}
                      id={name}
                      key={indexz}
                      className={[
                        classes.option,
                        "pLabelUp",
                        "enabledinputPLabel",
                        focusOn === indexz ? classes.activeOption : null,
                      ].join(" ")}
                      onMouseDown={(e) => optionClickHandler(e, option, indexz)}
                      style={forceLtr ? { textAlign: "end" } : { textAlign: "start" }}
                    >
                      {renderLabelOptions(option)}
                    </p>
                  ))
                ) : txt?.trim()?.length < indexToSendRequestAfterThat ? (
                  <p
                    className={[classes.option, "pLabelUp", "enabledinputPLabel"].join(" ")}
                    style={forceLtr ? { textAlign: "end" } : { textAlign: "start" }}
                  >
                    {forceLtr ? "Please type to search" : "لطفا تایپ کنید"}
                  </p>
                ) : (
                  <p
                    className={[classes.option, "pLabelUp", "enabledinputPLabel"].join(" ")}
                    style={forceLtr ? { textAlign: "end" } : { textAlign: "start" }}
                  >
                    {forceLtr ? "No option is available" : "گزینه ای یافت نشد"}
                  </p>
                )}
              </div>,
              document.body,
            )
          : null}
        {validityName ? (
          <ValidationErrors validity={validityName?.isValid} message={validityName?.msg} />
        ) : null}
      </div>
    </>
  );
};

export default Suggestion;
